/**
 * ActiveUserTableHeader - Header row for active users table
 */
import React from 'react';
import { TableHeaderCell, StaticHeaderCell } from './TableHeaderCell';

export function ActiveUserTableHeader({ 
  getSortIcon, 
  handleSort, 
  tableConfig,
  tableId,
  isEnterpriseSection 
}) {
  const {
    showTrialEnd = false,
    showSubscriptionInfo = true,
    showCancellationDate = false,
    showDeletionDate = false,
    showNextDecisionDate = false,
    showFreeUser = true,
    showActions = true
  } = tableConfig;

  return (
    <thead className="bg-gray-50 dark:bg-gray-800/50">
      <tr>
        {/* Name Column */}
        <TableHeaderCell
          label="Name"
          sortKey="displayName"
          tooltipContent="User's display name or email if no name is set"
          tooltipId={`name-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {isEnterpriseSection && (
          <TableHeaderCell
            label="Enterprise"
            sortKey="enterprise"
            tooltipContent="Organization or company association"
            tooltipId={`enterprise-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Email Column */}
        <TableHeaderCell
          label="Email"
          sortKey="email"
          tooltipContent="User's email address used for login and communication"
          tooltipId={`email-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {/* Phone Column */}
        <TableHeaderCell
          label="Phone"
          sortKey="phone"
          tooltipContent="User's phone number (E.164 or national)"
          tooltipId={`phone-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {/* Role Column */}
        <TableHeaderCell
          label="Role"
          sortKey="role"
          tooltipContent="User's role (campaign/corporate)"
          tooltipId={`role-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {/* Time Until End Column (conditional) */}
        {showTrialEnd && (
          <TableHeaderCell
            label="Time Until End"
            sortKey="trialEndDate"
            tooltipContent="How much time is left in the trial"
            tooltipId={`time-until-end-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Next Decision Date Column (conditional) */}
        {showNextDecisionDate && (
          <TableHeaderCell
            label="Next Decision Date"
            sortKey="nextDecisionDate"
            tooltipContent="Next important date: billing renewal or subscription cancellation"
            tooltipId={`next-decision-date-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Member Since Column */}
        <TableHeaderCell
          label="Member Since"
          sortKey="createdAt"
          tooltipContent="When this user account was created"
          tooltipId={`member-since-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {/* Last Active */}
        <TableHeaderCell
          label="Last Active"
          sortKey="lastActive"
          tooltipContent="When the user was last active on the platform"
          tooltipId={`last-active-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {/* Plan Interval Column (conditional) */}
        {showSubscriptionInfo && (
          <TableHeaderCell
            label="Subscription"
            sortKey="planInterval"
            tooltipContent="Billing frequency: Monthly or Annual subscription"
            tooltipId={`plan-interval-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Cancellation Date Column (conditional) */}
        {showCancellationDate && (
          <TableHeaderCell
            label="Date Cancelled"
            sortKey="cancellationDate"
            tooltipContent="When the user cancelled their subscription or when it ended"
            tooltipId={`cancel-date-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Deletion Date Column (conditional) */}
        {showDeletionDate && (
          <TableHeaderCell
            label="Date Deleted"
            sortKey="deletionDate"
            tooltipContent="When the user deleted their account"
            tooltipId={`delete-date-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Activity Metrics */}
        <TableHeaderCell
          label="Page Hits"
          sortKey="totalTemplatePageHits"
          tooltipContent="Number of template page views by this user"
          tooltipId={`page-hits-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Templates Used"
          sortKey="totalTemplatesEdited"
          tooltipContent="Number of templates edited/used by this user"
          tooltipId={`templates-used-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Video Templates"
          sortKey="totalVideoTemplatesEdited"
          tooltipContent="Number of video templates edited by this user"
          tooltipId={`video-templates-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Campaigns"
          sortKey="campaignsCreated"
          tooltipContent="Number of campaigns created by this user"
          tooltipId={`campaigns-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Live Support"
          sortKey="liveSupportClicks"
          tooltipContent="Clicks on Live Support (Send a Text)"
          tooltipId={`live-support-clicks-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Calendly"
          sortKey="bookCalendlyClicks"
          tooltipContent="Clicks on Book a Call"
          tooltipId={`book-calendly-clicks-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="RoboCent"
          sortKey="visitRobocentClicks"
          tooltipContent="Clicks on partner RoboCent link"
          tooltipId={`visit-robocent-clicks-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Website Page Views"
          sortKey="totalWebsitePageViews"
          tooltipContent="Number of visits to Websites library"
          tooltipId={`website-page-views-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Websites Used"
          sortKey="totalWebsitesVisited"
          tooltipContent="Clicks on website links in the Websites modal"
          tooltipId={`websites-used-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Website Calendly"
          sortKey="totalWebsiteCalendlyClicks"
          tooltipContent="Clicks on Schedule a Call inside Website modal"
          tooltipId={`website-calendly-clicks-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Survey Videos"
          sortKey="surveyVideosCollected"
          tooltipContent="Number of survey videos collected by this user"
          tooltipId={`survey-videos-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Stock Videos"
          sortKey="totalStockVideosDownloaded"
          tooltipContent="Number of stock videos downloaded by this user"
          tooltipId={`stock-videos-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Namespaces"
          sortKey="namespacesCount"
          tooltipContent="Number of namespaces this user belongs to"
          tooltipId={`namespaces-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Engagement"
          sortKey="currentStreak"
          tooltipContent="Final engagement metrics: current streak and monthly activity"
          tooltipId={`engagement-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Monthly Price"
          sortKey="monthlyPrice"
          tooltipContent="What this user pays per month for their subscription"
          tooltipId={`monthly-price-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Total Revenue"
          sortKey="totalRevenue"
          tooltipContent="Total lifetime revenue generated by this user"
          tooltipId={`total-revenue-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        <TableHeaderCell
          label="Customer Tenure"
          sortKey="monthsAsPayingCustomer"
          tooltipContent="How many months this user has been a paying customer"
          tooltipId={`months-customer-tooltip-${tableId}`}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
        />

        {!isEnterpriseSection && (
          <TableHeaderCell
            label="Enterprise"
            sortKey="enterprise"
            tooltipContent="Organization or company association"
            tooltipId={`enterprise-tooltip-${tableId}`}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
          />
        )}

        {/* Free User Column (conditional) */}
        {showFreeUser && (
          <StaticHeaderCell
            label="Free User"
            tooltipContent="Whether this user is marked as a free user"
            tooltipId={`free-user-tooltip-${tableId}`}
          />
        )}

        {/* Actions Column (conditional) */}
        {showActions && (
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Actions
          </th>
        )}
      </tr>
    </thead>
  );
}

